import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, X, Calendar, MapPin } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const ConfettiPiece = ({ style }) => <div className="confetti-piece" style={style} />;

const generateConfetti = (count = 40) =>
  Array.from({ length: count }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    animationDelay: `${Math.random() * 0.8}s`,
    animationDuration: `${0.8 + Math.random() * 1.2}s`,
    backgroundColor: ["#FF6B35", "#E85520", "#FF8C5A", "#f59e0b", "#10b981", "#3b82f6", "#ec4899"][
      Math.floor(Math.random() * 7)
    ],
    width: `${6 + Math.random() * 8}px`,
    height: `${6 + Math.random() * 8}px`,
    borderRadius: Math.random() > 0.5 ? "50%" : "2px",
    transform: `rotate(${Math.random() * 360}deg)`,
  }));

const OrderConfirmationModal = ({ show, orderDetails, onOk, onClose }) => {
  const navigate = useNavigate();
  const [confetti, setConfetti] = useState([]);
  const [checkAnimated, setCheckAnimated] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);

  useEffect(() => {
    if (show) {
      setConfetti(generateConfetti(50));
      setTimeout(() => setCheckAnimated(true), 300);
      setTimeout(() => setContentVisible(true), 600);
    } else {
      setConfetti([]);
      setCheckAnimated(false);
      setContentVisible(false);
    }
  }, [show]);

  if (!show) return null;

  const handleConfirmBooking = () => {
    if (onOk) onOk();
    navigate("/service-history");
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4 overflow-hidden">
      {confetti.map((c) => (
        <ConfettiPiece
          key={c.id}
          style={{
            position: "fixed",
            left: c.left,
            top: "-10px",
            width: c.width,
            height: c.height,
            backgroundColor: c.backgroundColor,
            borderRadius: c.borderRadius,
            transform: c.transform,
            animation: `confettiFall ${c.animationDuration} ${c.animationDelay} ease-in forwards`,
            zIndex: 60,
            pointerEvents: "none",
          }}
        />
      ))}

      <motion.div
        initial={{ opacity: 0, scale: 0.7, y: 40 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        className="bg-[#111111] border border-white/[0.10] rounded-3xl w-full max-w-2xl max-h-[90vh] shadow-2xl relative flex flex-col overflow-hidden"
      >
        {/* Orange glow border */}
        <div
          className="absolute inset-0 rounded-3xl pointer-events-none"
          style={{
            background: "linear-gradient(135deg, #FF6B35, #E85520, #FF8C5A)",
            padding: "1.5px",
            borderRadius: "1.5rem",
            WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
          }}
        />

        {/* Header */}
        <div className="relative bg-[#FF6B35]/10 border-b border-white/[0.08] p-6 rounded-t-3xl overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF6B35]/10 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-20 h-20 bg-[#FF6B35]/5 rounded-full translate-y-1/2 -translate-x-1/2 pointer-events-none" />

          <div className="flex justify-between items-start relative z-10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#FF6B35] rounded-2xl flex items-center justify-center shadow-lg shadow-[#FF6B35]/30">
                <CheckCircle className="h-7 w-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-black text-white tracking-tight">Order Confirmed 🎉</h3>
                <p className="text-[#FF6B35] text-sm font-medium">Your booking is locked in!</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-xl transition-all hover:rotate-90 text-gray-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto flex-1">
          {/* Animated Checkmark */}
          <div className="text-center py-4 relative">
            {checkAnimated && (
              <>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-36 h-36 rounded-full bg-[#FF6B35]/10" style={{ animation: "ripple 1.5s ease-out infinite" }} />
                </div>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-28 h-28 rounded-full bg-[#FF6B35]/10" style={{ animation: "ripple 1.5s 0.3s ease-out infinite" }} />
                </div>
              </>
            )}
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={checkAnimated ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -20 }}
              transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.1 }}
              className="relative inline-flex items-center justify-center w-24 h-24 rounded-full bg-[#FF6B35] shadow-2xl shadow-[#FF6B35]/40"
            >
              <CheckCircle className="h-14 w-14 text-white drop-shadow-lg" />
              {["top-0 right-0", "bottom-0 left-0", "top-0 left-2", "bottom-1 right-1"].map((pos, i) => (
                <span
                  key={i}
                  className={`absolute ${pos} w-3 h-3 bg-yellow-400 rounded-full`}
                  style={{ animation: `sparkle 1s ${i * 0.15}s ease-out infinite alternate` }}
                />
              ))}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={contentVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl font-extrabold text-white mt-4 mb-1">Woohoo! 🙌</h2>
              <p className="text-gray-500 text-sm">Your service has been booked successfully</p>
            </motion.div>
          </div>

          {/* Order ID */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={contentVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 15 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="rounded-2xl p-5 bg-white/[0.04] border border-white/[0.08] relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-[#FF6B35]/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm font-semibold text-gray-400">🔖 Order ID</span>
              <span className="text-base font-bold text-[#FF6B35] tracking-widest font-mono">
                {orderDetails?.orderId || `#OKM${Date.now().toString().slice(-8)}`}
              </span>
            </div>
            <div className="h-px bg-white/[0.06] mb-3" />
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-gray-400">📋 Status</span>
              <span className="px-3 py-1 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded-full text-xs font-bold uppercase tracking-wide">
                ⏳ Pending Confirmation
              </span>
            </div>
          </motion.div>

          {/* Booking Details */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={contentVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 15 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <h4 className="font-bold text-white text-sm mb-3 flex items-center gap-2">
              <span className="w-1 h-4 bg-[#FF6B35] rounded-full inline-block" />
              Booking Details
            </h4>
            <div className="grid gap-2">
              {orderDetails?.selectedDate && orderDetails?.selectedTimeSlot && (
                <div className="flex items-center gap-3 p-3 bg-white/[0.04] border border-white/[0.06] rounded-xl hover:border-[#FF6B35]/20 transition-colors">
                  <div className="w-10 h-10 bg-[#FF6B35] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">Service Date & Time</p>
                    <p className="text-xs text-[#FF6B35] font-medium mt-0.5">
                      {orderDetails.selectedDate} at {orderDetails.selectedTimeSlot}
                    </p>
                  </div>
                </div>
              )}
              {orderDetails?.selectedStore && (
                <div className="flex items-center gap-3 p-3 bg-white/[0.04] border border-white/[0.06] rounded-xl hover:border-[#FF6B35]/20 transition-colors">
                  <div className="w-10 h-10 bg-[#FF6B35] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">Service Location</p>
                    <p className="text-xs text-[#FF6B35] font-medium mt-0.5">{orderDetails.selectedStore}</p>
                  </div>
                </div>
              )}
              {orderDetails?.bikeDetails && (
                <div className="flex items-center gap-3 p-3 bg-white/[0.04] border border-white/[0.06] rounded-xl hover:border-[#FF6B35]/20 transition-colors">
                  <div className="w-10 h-10 bg-[#FF6B35] rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">Vehicle</p>
                    <p className="text-xs text-[#FF6B35] font-medium mt-0.5">
                      {orderDetails.bikeDetails.manufacturer} {orderDetails.bikeDetails.model}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Selected Services */}
          {orderDetails?.cartItems?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={contentVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 15 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <h4 className="font-bold text-white text-sm mb-3 flex items-center gap-2">
                <span className="w-1 h-4 bg-[#FF6B35] rounded-full inline-block" />
                Selected Services
              </h4>
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-3 space-y-2">
                {orderDetails.cartItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -15 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * index }}
                    className="flex items-center gap-3 p-2 bg-white/[0.03] rounded-xl"
                  >
                    {item.imageUrl && (
                      <img
                        src={item.imageUrl}
                        alt={item.name}
                        className="w-10 h-10 object-cover rounded-lg flex-shrink-0"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )}
                    <div className="flex-1">
                      <p className="font-semibold text-white text-sm">{item.name}</p>
                      <p className="text-xs text-[#FF6B35] font-bold mt-0.5">₹{item.discountedPrice}</p>
                    </div>
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-4 w-4 text-green-400" />
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Info Note */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={contentVisible ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            className="p-4 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-2xl flex items-start gap-3"
          >
            <div className="w-8 h-8 bg-[#FF6B35]/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="h-4 w-4 text-[#FF6B35]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="font-bold text-[#FF6B35] text-sm mb-1">What's next?</p>
              <p className="text-xs text-gray-400 leading-relaxed">
                You'll receive a confirmation SMS shortly. Our team will contact you to confirm all booking details. 📱
              </p>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={contentVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ duration: 0.4, delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-3 pt-2"
          >
            <button
              onClick={onClose}
              className="flex-1 px-5 py-3.5 bg-white/[0.04] border border-white/[0.08] text-gray-400 font-semibold rounded-2xl hover:bg-white/[0.08] transition-all text-sm"
            >
              Close
            </button>
            <button
              onClick={handleConfirmBooking}
              className="flex-1 px-5 py-3.5 bg-[#FF6B35] hover:bg-[#E85520] text-white font-bold rounded-2xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-[#FF6B35]/20 relative overflow-hidden text-sm"
            >
              <div className="absolute inset-0 bg-white/10 -translate-x-full" style={{ animation: "shimmer 2s infinite" }} />
              <CheckCircle className="h-4 w-4 relative z-10" />
              <span className="relative z-10">View Service History</span>
            </button>
          </motion.div>
        </div>
      </motion.div>

      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          80% { opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        @keyframes ripple {
          0% { transform: scale(0.8); opacity: 0.6; }
          100% { transform: scale(1.8); opacity: 0; }
        }
        @keyframes sparkle {
          0% { transform: scale(0.5) translate(0, 0); opacity: 1; }
          100% { transform: scale(1.5) translate(4px, -4px); opacity: 0.4; }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-15deg); }
          100% { transform: translateX(300%) skewX(-15deg); }
        }
      `}</style>
    </div>
  );
};

export default OrderConfirmationModal;
